import { Car, CheckCircle, MapPin, ArrowRight, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const AuctionTransportPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Auction Transport East London | BCA & Manheim Collection | Hackney Delivery"
        description="Professional auction car transport from BCA Enfield, Manheim Colchester to Hackney & East London. Same-day delivery available. Call 07492 639223 for auction vehicle collection."
        keywords="auction car transport, BCA collection, Manheim transport, auction vehicle delivery, car auction collection hackney, auction transport east london, vehicle collection service"
      />
      <Header />

      <section className="bg-gradient-to-br from-amber-900 via-amber-800 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-amber-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🏆 Auction Vehicle Specialists
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Auction Transport from BCA & Manheim to East London
            </h1>
            <p className="text-xl text-slate-200 mb-8">
              Professional auction car collection and delivery to Hackney and East London. We collect from BCA Enfield, Manheim Colchester, Aston Barclay and all major auction houses. Same-day delivery available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-amber-900 hover:bg-slate-100">
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-900">
                  Get Auction Transport Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Auction Car Transport to Hackney & East London</h2>
            <p className="text-lg text-slate-700 mb-6">
              Buying a car at auction is cost-effective, but getting your vehicle from the auction house to Hackney requires professional transport. Ovawell Autos specializes in collecting vehicles from BCA, Manheim, and all major auction houses, delivering directly to your East London address.
            </p>

            <h3 className="text-2xl font-bold mb-4 mt-8">Auction Houses We Serve</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-amber-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">BCA Enfield</h4>
                  <p className="text-slate-600 mb-2">12 miles from Hackney</p>
                  <p className="text-sm text-slate-500">Running vehicle: £70-£90</p>
                  <p className="text-sm text-slate-500">Non-runner: £90-£120</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-amber-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">Manheim Colchester</h4>
                  <p className="text-slate-600 mb-2">45 miles from Hackney</p>
                  <p className="text-sm text-slate-500">Running vehicle: £120-£150</p>
                  <p className="text-sm text-slate-500">Non-runner: £150-£190</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-amber-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">Aston Barclay Chelmsford</h4>
                  <p className="text-slate-600 mb-2">30 miles from Hackney</p>
                  <p className="text-sm text-slate-500">Running vehicle: £100-£130</p>
                  <p className="text-sm text-slate-500">Non-runner: £130-£170</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-amber-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">All Other Auction Houses</h4>
                  <p className="text-slate-600 mb-2">Within 50 miles</p>
                  <p className="text-sm text-slate-500">Custom quotes provided</p>
                  <p className="text-sm text-slate-500">Same-day collection available</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">How Auction Transport Works</h3>
            <div className="space-y-4 mb-8">
              {[
                { step: "1", title: "Win Your Auction", desc: "Bid and win your vehicle at BCA, Manheim, or any auction house" },
                { step: "2", title: "Contact Us", desc: "Call or WhatsApp with auction details, lot number, and delivery address in Hackney" },
                { step: "3", title: "We Collect", desc: "We collect your vehicle from the auction house within their deadline" },
                { step: "4", title: "Direct Delivery", desc: "Same-day or next-day delivery to your Hackney or East London address" }
              ].map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-amber-600">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Delivery Areas from Auction Houses</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-8">
              {[
                "Hackney (all areas)",
                "Shoreditch & Hoxton",
                "Bethnal Green",
                "Stratford",
                "Leyton",
                "Walthamstow",
                "Islington",
                "Tower Hamlets",
                "Newham",
                "Barking & Ilford",
                "20-mile radius",
                "Custom locations"
              ].map((area, idx) => (
                <div key={idx} className="bg-white p-3 rounded border flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span className="text-sm">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Why Choose Us for Auction Transport?</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Same-day collection available",
                "Experienced with auction procedures",
                "Running and non-running vehicles",
                "Direct delivery to East London",
                "Competitive fixed prices",
                "Fully insured transport",
                "Professional handling",
                "Flexible collection times"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-amber-900 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Won a Car at Auction?</h2>
          <p className="text-xl mb-8">Professional collection and delivery to Hackney & East London</p>
          <a href={`tel:${companyInfo.phone}`}>
            <Button size="lg" className="bg-white text-amber-900 hover:bg-slate-100">
              Call {companyInfo.phone}
            </Button>
          </a>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
